import React, { useState } from "react";
import { CheckCircle2, ArrowRight } from "lucide-react";

const BUSINESS_TYPES = [
  "ITAD",
  "Refurbisher",
  "Buyback operator",
  "Electronics recycler",
  "Corporate IT",
  "Ecommerce reseller",
  "Other",
];

const DEVICE_VOLUMES = ["Under 100", "100–500", "500–2,000", "2,000–10,000", "10,000+", "Not sure"];

const PERKS = [
  "A pre-configured, plug-and-play station",
  "Hands-on onboarding",
  "A direct line to the team building it",
  "A say in the roadmap (multi-pass, certifications, full cycle)",
];

const fieldCls =
  "w-full rounded-xl border border-line bg-white px-4 py-2.5 text-sm text-brand-plum placeholder:text-faint outline-none transition-colors focus:border-brand-orchid focus:ring-2 focus:ring-brand-orchid/30";
const labelCls = "mb-1.5 block text-sm font-medium text-brand-plum";

const EraseRegister = () => {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    const form = e.target;
    const data = Object.fromEntries(new FormData(form).entries());
    data.formName = "Poofed pilot request";
    data.program = "poofed-pilot";

    setSubmitting(true);
    setError("");
    try {
      const res = await fetch("/contact.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `Request failed (${res.status})`);
      }
      setSubmitted(true);
      window.scrollTo({
        top: form.getBoundingClientRect().top + window.scrollY - 120,
        behavior: "smooth",
      });
    } catch (err) {
      setError(err.message || "Something went wrong. Please try again or email help@furloop.io.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="pilot" className="relative overflow-hidden bg-brand-bg font-body">
      <div
        className="pointer-events-none absolute -right-24 top-10 h-[26rem] w-[26rem] rounded-full bg-brand-orchid/20 blur-3xl"
        aria-hidden
      />
      <div className="relative mx-auto max-w-3xl px-6 py-20 lg:py-28">
        {/* header */}
        <div className="text-center">
          <span className="inline-flex items-center gap-2 rounded-full border border-line bg-white px-3.5 py-1.5 text-xs font-medium uppercase tracking-[0.16em] text-brand-plum">
            <span className="h-1.5 w-1.5 rounded-full bg-brand-magenta" />
            Pilot program
          </span>
          <h2 className="mt-5 font-display text-3xl font-semibold leading-tight tracking-tight text-brand-plum sm:text-4xl lg:text-5xl">
            Join the Furloop Poofed Pilot
          </h2>
          <p className="mx-auto mt-5 max-w-2xl text-base leading-relaxed text-ink sm:text-lg">
            We&apos;re onboarding a first wave of refurbishers and ITADs. Pilot partners get a head start on
            verified, hands-off erasure — and a direct say in where it goes next.
          </p>
          <ul className="mx-auto mt-6 flex max-w-2xl flex-wrap items-center justify-center gap-2 pl-0">
            {PERKS.map((p) => (
              <li
                key={p}
                className="inline-flex items-center gap-1.5 rounded-full border border-line bg-white px-3 py-1.5 text-xs font-medium text-brand-plum"
              >
                <CheckCircle2 className="h-3.5 w-3.5 text-success" />
                {p}
              </li>
            ))}
          </ul>
        </div>

        {/* card */}
        <div className="mt-10 rounded-3xl border border-line bg-white p-6 shadow-xl shadow-brand-plum/5 sm:p-8">
          {submitted ? (
            <div className="flex flex-col items-center py-10 text-center">
              <CheckCircle2 className="h-12 w-12 text-success" />
              <h3 className="mt-4 font-display text-2xl font-semibold text-brand-plum">
                Thanks — you&apos;re on the pilot list
              </h3>
              <p className="mt-3 max-w-md text-sm leading-relaxed text-muted">
                We&apos;ll review your details and reach out to confirm fit, station setup, and onboarding for
                the Furloop Poofed pilot program.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className={labelCls} htmlFor="name">Name</label>
                  <input id="name" name="name" required className={fieldCls} placeholder="Your name" />
                </div>
                <div>
                  <label className={labelCls} htmlFor="company">Company</label>
                  <input id="company" name="company" required className={fieldCls} placeholder="Company name" />
                </div>
                <div>
                  <label className={labelCls} htmlFor="email">Work email</label>
                  <input id="email" name="email" type="email" required className={fieldCls} placeholder="you@company.com" />
                </div>
                <div>
                  <label className={labelCls} htmlFor="country">Country</label>
                  <input id="country" name="country" className={fieldCls} placeholder="Country" />
                </div>
                <div>
                  <label className={labelCls} htmlFor="businessType">Business type</label>
                  <select id="businessType" name="businessType" className={fieldCls} defaultValue="">
                    <option value="" disabled>Select…</option>
                    {BUSINESS_TYPES.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelCls} htmlFor="devicesPerMonth">Devices wiped per month</label>
                  <select id="devicesPerMonth" name="devicesPerMonth" className={fieldCls} defaultValue="">
                    <option value="" disabled>Select…</option>
                    {DEVICE_VOLUMES.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                </div>
              </div>

              <div>
                <label className={labelCls} htmlFor="drives">Drive types you process</label>
                <input
                  id="drives"
                  name="drives"
                  className={fieldCls}
                  placeholder="NVMe & SATA SSDs, hard drives, self-encrypting drives, multi-drive PCs…"
                />
              </div>
              <div>
                <label className={labelCls} htmlFor="message">What does your erasure process look like today?</label>
                <textarea
                  id="message"
                  name="message"
                  rows={3}
                  className={fieldCls}
                  placeholder="Current tools, certificates you produce, compliance requirements, bottlenecks…"
                />
              </div>

              {error && (
                <p className="rounded-xl border border-danger/30 bg-danger/5 px-4 py-3 text-sm text-danger">
                  {error}
                </p>
              )}

              <button
                type="submit"
                disabled={submitting}
                className="group inline-flex w-full items-center justify-center gap-2 rounded-full bg-cta-gradient px-6 py-3.5 text-sm font-semibold text-white shadow-lg shadow-[#304BEF]/25 transition-transform duration-200 hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:translate-y-0"
              >
                {submitting ? "Sending…" : "Request Pilot Access"}
                {!submitting && (
                  <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" />
                )}
              </button>
              <p className="text-center text-xs text-muted">
                Want the full platform too? Erasure is one stage of Furloop&apos;s end-to-end recovery cycle.
              </p>
            </form>
          )}
        </div>
      </div>
    </section>
  );
};

export default EraseRegister;
